import React, { useState, useEffect } from "react";
import { TextField, Button, Box, Typography, Stack, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { getDatabase, ref, push, serverTimestamp } from 'firebase/database';
import * as yup from 'yup';
import { getAuth } from 'firebase/auth';
import app from '../../services/firebase';
import { useNavigate } from 'react-router-dom';
import { ResourceType, ResourceStatus } from '../../datamodels/Resources'; // Import the data model

function AddResourceForm() {
    const [formData, setFormData] = useState({
        type: ResourceType.Website,
        title: '',
        description: '',
        url: '',
        visibility: 'private', // Default to private
        status: ResourceStatus.Proposed, // create a const for the possible statuses
        reviewStatement: "to be reviewed", // used to record admin review comments
    });

    const [errors] = useState({
        title: '',
        description: '',
        url: {
            protocol: '',
            domain: '',
        },
    });
    const [submitError, setSubmitError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [resources, setResources] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);

    const database = getDatabase(app);
    const auth = getAuth(app);
    const navigate = useNavigate();

    useEffect(() => {
        console.log("****** AddResourceForm *******")
        const unsubscribe = auth.onAuthStateChanged(user => setCurrentUser(user));
        return () => unsubscribe();
    }, [auth]);

    function getFaviconUrl(resourceUrl) {
      try {
        const url = new URL(resourceUrl);
        console.log("**** Favicon URL `${url.protocol}//${url.hostname}/favicon.ico`;")
        return `${url.protocol}//${url.hostname}/favicon.ico`;
      } catch (e) {
        return null; // Invalid URL
      }
    }

    const faviconUrl = getFaviconUrl(formData.url);
    // console.log("Favicon URL: ", faviconUrl);

    // Yup schema fro validation

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({ ...prevData, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSubmitError(null);
        console.log("****** handleSubmit *******");
        console.log(formData);

        try {
            if (!currentUser) {
                setSubmitError("You must be logged in to add a resource.");
                setLoading(false);
                return;
            }

            const faviconUrl = getFaviconUrl(formData.url);

            const newResource = {
                type: formData.type,
                title: formData.title,
                description: formData.description,
                url: formData.url,
                visibility: formData.visibility,
                status: ResourceStatus.Proposed, // Set initial status for moderation
                imageUrl: faviconUrl,
                uid: currentUser.uid, // Add owner's UID
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            };

            const resourcesRef = ref(database, 'resources');
            const newResourceRef = await push(resourcesRef, newResource);

            const firebaseGeneratedId = newResourceRef.key;
            newResource.id = firebaseGeneratedId;
            setResources((prevResources) => [...prevResources, newResource]);
            
            navigate('/resources'); // Navigate back to the list on success
        } catch (err) {
            setSubmitError(err.message || "An unexpected error occurred.");
        }
    };

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ mt: 2 }}>
            <div>
                <Typography variant="h6" gutterBottom >
                    Add New Resource
                </Typography>
                {
                    submitError && (
                        <Typography variant="body2" color="error" align="center" sx={{ color: 'red' }
                        }>
                            {submitError}
                        </Typography>
                    )}
                <Stack spacing={2}>
                    <TextField
                        label="Title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        required
                        fullWidth
                    />
                    <TextField
                        label="Description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        required
                        fullWidth
                        multiline
                        rows={4}
                    />
                    <Box
                        component="img"
                        src={faviconUrl}
                        alt="Favicon"
                        sx={{ width: 24, height: 24, marginBottom: 2 }}
                    />
                    <TextField
                        label="URL"
                        name="url"
                        type="url"
                        value={formData.url}
                        onChange={handleChange}
                        fullWidth
                    />
                    <FormControl component="fieldset">
                        <FormLabel component="legend">Visibility</FormLabel>
                        <RadioGroup
                            row
                            aria-label="visibility"
                            name="visibility"
                            value={formData.visibility}
                            onChange={handleChange}
                        >
                            <FormControlLabel value="private" control={<Radio />} label="Private" />
                            <FormControlLabel value="public" control={<Radio />} label="Public (requires admin approval)" />
                        </RadioGroup>
                    </FormControl>
                    <Button type="submit" variant="contained" >
                        Add Resource
                    </Button>
                </Stack>
            </div>
        </Box>
    );


}

export default AddResourceForm;
